/**
 * Application Layout Component
 * Provides the main layout structure with sidebar navigation.
 * WCAG 2.1 AA compliant with skip links, keyboard navigation, and ARIA landmarks.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { Outlet, NavLink, useLocation } from 'react-router';
import { useRef, useState, useEffect, useCallback } from 'react';
import { X, Menu, AlertCircle, ChevronDown, ArrowRightLeft, LayoutDashboard } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores';
import { useTheme, useNavBadges, useEnvironments } from '@/hooks';
import { Badge, Button, UserMenu, EnvironmentSelector } from '@/components/ui';

import { TopBar } from './TopBar';
import { NotificationContainer } from './ui/NotificationContainer';

const MOBILE_BREAKPOINT = 1024;

const TOP_NAVIGATION = [{ href: '/dashboard', icon: LayoutDashboard, nameKey: 'nav.overview' }] as const;

/**
 * Main layout component with sidebar navigation.
 * Implements WCAG 2.1 AA accessibility features including:
 * - Skip navigation links
 * - ARIA landmarks (banner, navigation, main)
 * - Keyboard shortcuts (Ctrl+/ for help)
 * - Focus management on route changes
 *
 * @returns { JSX.Element } The layout component.
 */
export const Layout = () => {
  const { t } = useTranslation();
  const { dlq: dlqCount, mappings: mappingCount } = useNavBadges();

  /** Navigation sections with collapsible groups (badges are dynamic). */
  const navigationSections = [
    {
      defaultExpanded: true,
      titleKey: 'nav.dataPipeline',
      items: [
        { href: '/mappings', badge: mappingCount, icon: ArrowRightLeft, nameKey: 'nav.mappings' },
        { href: '/dlq', badge: dlqCount, icon: AlertCircle, nameKey: 'nav.deadLetterQueue' },
      ],
    },
  ];

  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const { retailers, selectEnvironment, isLoading: environmentsLoading } = useEnvironments();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navigationSections.map((section) => [section.titleKey, section.defaultExpanded])),
  );
  useTheme();

  const toggleSection = useCallback((sectionTitle: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  }, []);

  const handleSkipToMain = useCallback(() => {
    mainRef.current?.focus();
  }, []);

  const handleSkipToNav = useCallback(() => {
    navRef.current?.focus();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Focus main content when route changes
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);

        return;
      }

      const isMac = /mac/i.test(navigator.userAgent);
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      if (modifier && event.key === '/') {
        event.preventDefault();
        const helpEvent = new CustomEvent('show-keyboard-shortcuts');
        window.dispatchEvent(helpEvent);
      }

      if (modifier && event.key === 'b') {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar, sidebarOpen, setSidebarOpen]);

  return (
    <div className="min-h-screen bg-background">
      <button type="button" className="skip-link" onClick={handleSkipToMain}>
        {t('skipToMainContent')}
      </button>
      <button type="button" className="skip-link left-[180px]" onClick={handleSkipToNav}>
        {t('skipToNavigation')}
      </button>

      {/* Mobile header */}
      <header className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center gap-3 border-b bg-card px-4 lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          aria-expanded={sidebarOpen}
          aria-controls="sidebar-navigation"
          aria-label={sidebarOpen ? t('aria.closeSidebar') : t('aria.openSidebar')}
        >
          {sidebarOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
        </Button>
        <span className="font-semibold text-foreground">{t('nexusAdmin')}</span>
      </header>

      <TopBar className="hidden lg:flex" />

      {/* Sidebar */}
      <aside
        id="sidebar-navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-72 transform border-r border-sidebar-border bg-sidebar transition-transform duration-200 ease-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label={t('aria.primaryNavigation')}
        aria-hidden={isMobile && !sidebarOpen}
      >
        <div className="flex h-full flex-col pt-16">
          {/* Environment selector */}
          <div className="border-b border-sidebar-border px-4 py-3.5">
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {t('nav.selectRetailer')}
            </div>
            <EnvironmentSelector
              retailers={retailers}
              isLoading={environmentsLoading}
              onEnvironmentChange={selectEnvironment}
            />
          </div>

          {/* Navigation menu */}
          <nav
            id="main-navigation"
            ref={navRef}
            className="flex-1 overflow-y-auto px-3 pt-3 pb-4"
            aria-label={t('aria.mainNavigation')}
            tabIndex={-1}
          >
            {/* Top-level navigation items */}
            {TOP_NAVIGATION.map((item) => (
              <NavLink
                key={item.nameKey}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] transition-all duration-200 ease-out',
                    isActive
                      ? 'sidebar-nav-active bg-sidebar-accent/70 text-foreground font-semibold'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      size={18}
                      strokeWidth={isActive ? 2.25 : 2}
                      aria-hidden="true"
                      className={cn(
                        'shrink-0 transition-all duration-200 text-muted-foreground',
                        isActive && 'text-primary',
                      )}
                    />
                    <span aria-current={isActive ? 'page' : undefined}>{t(item.nameKey)}</span>
                  </>
                )}
              </NavLink>
            ))}

            {/* Collapsible navigation sections */}
            {navigationSections.map((section) => (
              <div key={section.titleKey} className="mt-5">
                <button
                  type="button"
                  onClick={() => toggleSection(section.titleKey)}
                  className="flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground transition-colors duration-150 hover:text-foreground"
                  aria-expanded={expandedSections[section.titleKey]}
                >
                  {t(section.titleKey)}
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    aria-hidden="true"
                    className={cn(
                      'shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
                      expandedSections[section.titleKey] ? 'rotate-180' : 'rotate-0',
                    )}
                  />
                </button>

                {expandedSections[section.titleKey] && (
                  <div className="mt-1 flex flex-col gap-0.5">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.nameKey}
                        to={item.href}
                        className={({ isActive }) =>
                          cn(
                            'relative flex items-center gap-3 rounded-lg py-2.5 pl-3 pr-3 text-[13.5px] transition-all duration-200 ease-out',
                            isActive
                              ? 'sidebar-nav-active bg-sidebar-accent/70 text-foreground font-semibold'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-foreground',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              size={18}
                              strokeWidth={isActive ? 2.25 : 2}
                              aria-hidden="true"
                              className={cn(
                                'shrink-0 transition-all duration-200 text-muted-foreground',
                                isActive && 'text-primary',
                              )}
                            />
                            <span aria-current={isActive ? 'page' : undefined}>{t(item.nameKey)}</span>
                            {'badge' in item && item.badge !== undefined && (
                              <Badge variant="info" className="ml-auto text-[10px] px-1.5 py-0">
                                {item.badge.toLocaleString()}
                              </Badge>
                            )}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* User menu — sidebar footer */}
          <div className="border-t border-sidebar-border p-3">
            <UserMenu />
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-20 cursor-default bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label={t('aria.closeSidebarOverlay')}
          tabIndex={-1}
        />
      )}

      <main
        id="main-content"
        ref={mainRef}
        className="min-h-screen transition-all duration-200 ease-in-out lg:ml-72 pt-14 lg:pt-16 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 bg-background"
        tabIndex={-1}
        aria-label={t('aria.mainContent')}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      <NotificationContainer />
    </div>
  );
};
